<script setup>
    import { computed, watch, watchEffect } from 'vue'
    import ModulVerantwortliche from '@/js/components/Modul/ModulVerantwortliche.vue'
    import Lehrende from '@/js/components/Modul/Lehrende.vue'
    import Fachbereich from '@/js/components/Modul/Fachbereich.vue'
    import VeranstaltungsOrtSlotted from '@/js/components/Modul/VeranstaltungsOrtSlotted.vue'
    import Ects from '@/js/components/Modul/Ects.vue'
    import Benotung from '@/js/components/Modul/Benotung.vue'
    import SemesterWochenStunden from '@/js/components/Modul/SemesterWochenStunden.vue'
    import EmpfohlenesFachsemester from '@/js/components/Modul/EmpfohlenesFachsemester.vue'
    import ModulDauer from '@/js/components/Modul/ModulDauer.vue'
    import ModulTyp from '@/js/components/Modul/ModulTyp.vue'
    import ModulLehrsprache from '@/js/components/Modul/Lehrsprache.vue'
    import ZwingendeVoraussetzungen from '@/js/components/Modul/ZwingendeVoraussetzungen.vue'
    import EmpfohleneVoraussetzungen from '@/js/components/Modul/EmpfohleneVoraussetzungen.vue'
    import Lernziele from '@/js/components/Modul/Lernziele.vue'
    import Angebotsfrequenz from '@/js/components/Modul/Angebotsfrequenz.vue'
    import { useModuleAccess } from '@/js/components/Common/useModuleAccess'
    import ModulInhalte from '@/js/components/Modul/ModulInhalte.vue'
    import VoraussetzungPruefung from '@/js/components/Modul/VoraussetzungPruefung.vue'
    import VoraussetzungVergabeEcts from '@/js/components/Modul/VoraussetzungVergabeEcts.vue'
    import Medienform from '@/js/components/Modul/Medienform.vue'
    import DokumentLinks from '@/js/components/Modul/DokumentLinks.vue'
    import Literatur from '@/js/components/Modul/Literatur.vue'
    import GefoerderteKompetenzen from '@/js/components/Modul/GefoerderteKompetenzen.vue'
    import ModulStruktur from '@/js/components/Modul/ModulStruktur.vue'
    import { useEditStateStore } from '@/js/stores/editState'
    import { useModuleStore, useTooltipStore } from '@/js/stores'

    const props = defineProps({
        modulId: {
            type: String,
            required: true
        }
    })

    const moduleStore = useModuleStore()
    const tooltipStore = useTooltipStore()
    const editStore = useEditStateStore()

    const {
        updateModuleAccess,
        isSFBForStudiengang,
        isSDForFachbereich,
        isModulverantwortlicherForModule,
        isLehrenderForModule
    } = useModuleAccess()

    const modul = computed(() => moduleStore.getModuleById(props.modulId))

    const isModulEditable = computed(() => {
        const semester = modul.value?.semester
        if (!semester?.typ || !semester?.jahr) return true

        const now = new Date()
        const month = now.getMonth() + 1
        const year = now.getFullYear()

        let currentTyp, currentJahr
        if (month >= 4 && month <= 9) {
            currentTyp = 'SoSe'
            currentJahr = year
        } else if (month >= 10) {
            currentTyp = 'WiSe'
            currentJahr = year
        } else {
            currentTyp = 'WiSe'
            currentJahr = year - 1
        }

        const { typ: modulTyp, jahr: modulJahr } = semester
        if (modulJahr !== currentJahr) return modulJahr > currentJahr
        if (modulTyp === currentTyp) return true

        return modulTyp === 'WiSe' && currentTyp === 'SoSe'
    })

    const language = computed(() => {
        const l = modul.value?.lehrsprache?.toLowerCase() ?? ''
        return ['englisch', 'english'].includes(l) ? 'englisch' : 'deutsch'
    })

    watch(
        () => props.modulId,
        () => editStore.resetAll()
    )
    const isAdmin = computed(() => store.getters['security/isAdmin'])

    watchEffect(() => {
        updateModuleAccess(modul.value, store.getters['security/currentUser'])
    })

    const saveModulVerantwortlicher = (modulverantwortliche) => {
        moduleStore.saveModulverantwortliche(props.modulId, modulverantwortliche)
    }

    const saveLehrende = (lehrendeIds) => {
        moduleStore.saveLehrende(props.modulId, lehrendeIds)
    }
    const saveVeranstaltungsOrt = (newOrt) => {
        moduleStore.saveVeranstaltungsOrt(props.modulId, newOrt)
    }

    const saveModulDauer = (newModulDauer) => {
        moduleStore.saveModulDauer(props.modulId, newModulDauer)
    }

    const saveBenotung = (newBenotung) => {
        moduleStore.saveBenotung(props.modulId, newBenotung)
    }

    const saveZwingendeVoraussetzungen = (newVoraussetzungen) => {
        moduleStore.saveZwingendeVoraussetzungen(props.modulId, newVoraussetzungen)
    }

    const saveEmpfohleneVoraussetzungen = (newVoraussetzungen) => {
        moduleStore.saveEmpfohleneVoraussetzungen(props.modulId, newVoraussetzungen)
    }

    const saveLernziele = (newLernziele) => {
        moduleStore.saveLernziele(props.modulId, newLernziele)
    }

    const saveAngebotsfrequenz = (newValue) => {
        moduleStore.saveAngebotsfrequenz(props.modulId, newValue)
    }

    const saveModulInhalte = (newValue) => {
        moduleStore.saveModulInhalte(props.modulId, newValue)
    }

    const saveVoraussetzungPruefung = (newValue) => {
        moduleStore.saveVoraussetzungPruefung(props.modulId, newValue)
    }

    const saveVoraussetzungEcts = (newValue) => {
        moduleStore.saveVoraussetzungEcts(props.modulId, newValue)
    }

    const saveMedienform = (newValue) => {
        moduleStore.saveMedienform(props.modulId, newValue)
    }

    const saveDokumentLinks = (newValue) => {
        moduleStore.saveDokumentlinks(props.modulId, newValue)
    }

    const saveLiteratur = (newValue) => {
        moduleStore.saveLiteratur(props.modulId, newValue)
    }

    const saveGefoerderteKompetenzen = (payload) => {
        moduleStore.saveGefoerderteKompetenzen(props.modulId, payload)
    }

    const saveModulStruktur = (payload) => {
        moduleStore.saveModulstruktur(props.modulId, payload)
    }
    const saveEcts = (payload) => {
        moduleStore.saveEcts(props.modulId, payload)
    }
    const saveSws = (payload) => {
        moduleStore.saveSws(props.modulId, payload)
    }
    const saveModultyp = (payload) => {
        moduleStore.saveModultyp(props.modulId, payload)
    }
    const saveLehrsprache = (payload) => {
        moduleStore.saveLehrsprache(props.modulId, payload)
    }
    const saveEmpfohlenesFachsemester = (payload) => {
        moduleStore.saveEmpfohlenesFachsemester(props.modulId, payload)
    }
    const saveFachbereich = (payload) => {
        moduleStore.saveFachbereich(props.modulId, payload)
    }
</script>

<template>
    <div class="module-fields d-flex flex-column flex-nowrap">
        <modul-verantwortliche
            :modul-verantwortliche="modul.modulverantwortliche"
            :allow-edit="isAdmin || (isModulEditable && (isSDForFachbereich || isSFBForStudiengang))"
            :tooltip="tooltipStore.getTooltipForField('modulverantwortliche')?.text"
            :language="language"
            @modul-verantwortlicher-saved="saveModulVerantwortlicher"
        />

        <lehrende
            :lehrende="modul.lehrende"
            :allow-edit="
                isAdmin ||
                (isModulEditable && (isSDForFachbereich || isSFBForStudiengang || isModulverantwortlicherForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('lehrende')?.text"
            :language="language"
            @lehrende-saved="saveLehrende"
        />

        <fachbereich
            :fachbereich-id="modul.fachbereichId"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('fachbereich')?.text"
            :language="language"
            @fachbereich-saved="saveFachbereich"
        />

        <veranstaltungs-ort-slotted
            :veranstaltungs-ort="modul.veranstaltungsOrt"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('veranstaltungsort')?.text"
            :language="language"
            @veranstaltungs-ort-saved="saveVeranstaltungsOrt"
        />

        <ects
            :ects="modul.ects"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('ects')?.text"
            :language="language"
            @ects-saved="saveEcts"
        />

        <benotung
            :benotung="modul.benotung"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('benotung')?.text"
            :language="language"
            @benotung-saved="saveBenotung"
        />

        <semester-wochen-stunden
            :sws="modul.semesterwochenstunden"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('semesterwochenstunden')?.text"
            :language="language"
            @sws-saved="saveSws"
        />

        <angebotsfrequenz
            :angebots-frequenz="modul.angebotsfrequenz"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('angebotsfrequenz')?.text"
            :language="language"
            @angebots-frequenz-saved="saveAngebotsfrequenz"
        />

        <empfohlenes-fachsemester
            :empfohlenes-fachsemester="modul.empfohlenesFachsemester"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('empfohlenesfachsemester')?.text"
            :language="language"
            @empfohlenes-fachsemester-saved="saveEmpfohlenesFachsemester"
        />

        <modul-dauer
            :modul-dauer="modul.moduldauer"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('moduldauer')?.text"
            :language="language"
            @modul-dauer-saved="saveModulDauer"
        />

        <modul-typ
            :modul-typ="modul.modultyp"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('modultyp')?.text"
            :language="language"
            @modul-typ-saved="saveModultyp"
        />

        <modul-lehrsprache
            :lehrsprache="modul.lehrsprache"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('lehrsprache')?.text"
            :language="language"
            @modul-lehrsprache-saved="saveLehrsprache"
        />

        <zwingende-voraussetzungen
            :voraussetzung="modul.zwingendeVoraussetzungen"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('zwingendevoraussetzungen')?.text"
            :language="language"
            @zwingende-voraussetzung-saved="saveZwingendeVoraussetzungen"
        />

        <empfohlene-voraussetzungen
            :voraussetzung="modul.empfohleneVoraussetzungen"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('empfohlenevoraussetzungen')?.text"
            :language="language"
            @empfohlene-voraussetzung-saved="saveEmpfohleneVoraussetzungen"
        />

        <voraussetzung-pruefung
            :voraussetzungen="modul.voraussetzungPruefungsZulassung"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('voraussetzungpruefung')?.text"
            :language="language"
            @voraussetzungen-pruefung-saved="saveVoraussetzungPruefung"
        />

        <voraussetzung-vergabe-ects
            :voraussetzungen-ects="modul.voraussetzungenVergabeEcts"
            :allow-edit="isAdmin"
            :tooltip="tooltipStore.getTooltipForField('voraussetzungvergabeects')?.text"
            :language="language"
            @voraussetzungen-ects-saved="saveVoraussetzungEcts"
        />

        <modul-struktur
            :lehr-formen="modul.modulStruktur.lehrformen"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('modulstruktur')?.text"
            :language="language"
            @lehrformen-saved="saveModulStruktur"
        />

        <lernziele
            :lernziele="modul.lernziele"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('lernziele')?.text"
            :language="language"
            @lernziele-saved="saveLernziele"
        />

        <gefoerderte-kompetenzen
            :gefoerderte-kompetenzen="modul.gefoerderteKompetenzen"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('gefoerdertekompetenzen')?.text"
            :language="language"
            @gefoerderte-kompetenzen-saved="saveGefoerderteKompetenzen"
        />

        <modul-inhalte
            :modul-inhalte="modul.inhalte"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('modulinhalte')?.text"
            :language="language"
            @modul-inhalte-saved="saveModulInhalte"
        />

        <literatur
            :literatur="modul.literatur"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('literatur')?.text"
            :language="language"
            @literatur-saved="saveLiteratur"
        />

        <medienform
            :medienform="modul.medienformen"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('medienform')?.text"
            :language="language"
            @medienform-saved="saveMedienform"
        />

        <dokument-links
            :links="modul.dokumentLinks"
            :allow-edit="
                isAdmin ||
                (isModulEditable &&
                    (isSDForFachbereich ||
                        isSFBForStudiengang ||
                        isModulverantwortlicherForModule ||
                        isLehrenderForModule))
            "
            :tooltip="tooltipStore.getTooltipForField('dokumentlinks')?.text"
            :language="language"
            @links-saved="saveDokumentLinks"
        />
    </div>
</template>

<script></script>

<style scoped></style>
